import React, { memo } from "react";
import { format, isSameDay } from "date-fns";
import { cn } from "@racoon/ui";
import { getDriverName, getShiftWindow } from "../lib/utils";
import { ConnectProto } from "@racoon/api";
import { useT, Namespace } from "@racoon/i18n";

interface DriverInfoProps {
  shifts: ConnectProto.DriverOnShift[];
  cancelledShiftIds?: Set<bigint>;
  /** The picked period spans several days, so same-day shifts need their date too. */
  showDates?: boolean;
  selectedShiftId?: string;
  onShiftSelect?: (shiftId: string) => void;
}

/** One row per shift: its actual start, its end (or "now") and the driver. */
const DriverInfoComponent: React.FC<DriverInfoProps> = ({
  shifts,
  cancelledShiftIds,
  showDates,
  selectedShiftId,
  onShiftSelect,
}) => {
  const t = useT();

  if (!shifts?.length) {
    return <div className="text-xs text-gray-500">{t(Namespace.GpsTracking, "noTripData")}</div>;
  }

  return (
    <>
      {shifts.map((shift) => {
        const { from, to } = getShiftWindow(shift);
        // An open shift ends "now", so one started yesterday still gets its date.
        const crossesDays = from && !isSameDay(from, to ?? new Date());
        const dateFormat = showDates || crossesDays ? "HH:mm dd.MM" : "HH:mm";

        const startStr = from ? format(from, dateFormat) : "—";
        const endStr = to ? format(to, dateFormat) : t(Namespace.GpsTracking, "currentTime");

        const shiftId = String(shift.id);
        const isSelected = selectedShiftId === shiftId;

        return (
          <div
            className={cn(
              "text-sm rounded px-2 py-1 transition-colors",
              isSelected
                ? "bg-brand-blue text-white"
                : "text-gray-600 hover:bg-brand-blue-light cursor-pointer",
            )}
            key={shiftId}
            onClick={(e) => {
              e.stopPropagation();
              onShiftSelect?.(shiftId);
            }}
          >
            {/* One line when it fits; otherwise the whole name drops below the time. */}
            <div className="flex flex-wrap justify-between gap-x-2">
              <span className="whitespace-nowrap">
                {startStr} - {endStr}
              </span>
              <span className="whitespace-nowrap">{getDriverName(shift) || "—"}</span>
            </div>
            {cancelledShiftIds?.has(shift.id) && (
              <div className={cn("text-xs", isSelected ? "text-white" : "text-brand-red")}>
                {t(Namespace.GpsTracking, "routesCancelled")}
              </div>
            )}
          </div>
        );
      })}
    </>
  );
};

export const DriverInfo = memo(DriverInfoComponent);
